import { useState, useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ExerciseCard } from "@/components/ExerciseCard";
import { MasteryCheck } from "@/components/MasteryCheck";
import { HoldTimer } from "@/components/HoldTimer";
import { ProgressBar } from "@/components/ProgressBar";
import { findDay, dayCompletion, itemId, getProgram, getActiveProgramId, isPhaseUnlocked, isProgramVisible } from "@/data/program";
import { ArrowLeft, CheckCheck, RotateCcw, Loader2 } from "lucide-react";

const CRIT_KEYS = ["pain", "control", "quality"];

export default function Session() {
  const { dayId } = useParams();
  const navigate = useNavigate();
  const { user, completed, toggleItem, setManyItems, bumpStats, progressLoaded } = useAuth();
  const [timerExercise, setTimerExercise] = useState(null);
  const [unlockedMsg, setUnlockedMsg] = useState(null); // progressionId of just-unlocked exercise
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmConclude, setConfirmConclude] = useState(false);

  const program = getProgram(getActiveProgramId());

  useEffect(() => {
    if (program && !isProgramVisible(program, user?.email)) {
      navigate("/select", { replace: true });
    }
  }, [program, user, navigate]);

  const day = findDay(program, dayId);
  if (!day) return null;

  if (!progressLoaded)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-[#fa003f]" size={28} />
      </div>
    );

  const phaseIndex = program?.phases?.findIndex((p) => p.id === day.phase?.id) ?? -1;
  if (phaseIndex >= 0 && !isPhaseUnlocked(program, phaseIndex, completed))
    return <Navigate to="/program" replace />;

  const levelId = day.phase?.id;
  const comp = dayCompletion(day, completed);
  const allDone = comp.done === comp.total;
  const ids = day.exercises.map((_, i) => itemId(day.id, i));

  const handleConclude = async () => {
    const stadioId = day.phase.id;
    const isSessionB = day.id.endsWith("-b");
    const markerId = `${stadioId}-next-b`;
    // Count checked exercises before clearing them
    const doneCount = ids.filter((id) => completed.has(id)).length;
    // Sequence: bump stats → clear checkmarks → rotate A/B pointer
    await bumpStats(1, doneCount);
    await setManyItems(ids, false);
    await toggleItem(markerId, !isSessionB);
    setConfirmConclude(false);
    navigate("/");
  };

  return (
    <div className="animate-slideup">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-zinc-800 px-5 pt-12 pb-4">
        <button
          data-testid="session-back"
          onClick={() => navigate(-1)}
          className="mb-2 flex items-center gap-1 text-sm uppercase tracking-wider text-zinc-500"
        >
          <ArrowLeft size={18} /> Indietro
        </button>
        <p className="font-head text-[11px] uppercase tracking-widest text-[#fa003f]">
          {day.phase.name} · {day.week.name}
        </p>
        <div className="flex items-end justify-between">
          <h1 className="font-head text-4xl font-black uppercase tracking-tight text-white">
            {day.label}
          </h1>
          <span className="font-head text-2xl text-zinc-400 tabular-nums">
            {comp.done}/{comp.total}
          </span>
        </div>
        {day.subtitle && (
          <p className="text-sm text-zinc-500 -mt-0.5">{day.subtitle}</p>
        )}
        <div className="mt-2">
          <ProgressBar pct={comp.pct} glow />
        </div>
      </div>

      {/* Exercise list */}
      <div className="px-5 pt-4 space-y-2.5">
        {program?.progressionType === "mastery" && (
          confirmReset ? (
            <div className="border border-zinc-700 bg-[#141414] px-4 py-3 space-y-2">
              <p className="text-sm text-zinc-300 leading-snug">
                Azzerare le spunte di questa sessione?{" "}
                <span className="text-zinc-500">I tuoi progressi sui movimenti chiave restano salvati.</span>
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { setManyItems(ids, false); setConfirmReset(false); }}
                  className="flex-1 h-10 bg-[#fa003f] text-white font-head text-sm font-bold uppercase tracking-wide active:scale-[0.98] transition-transform"
                >
                  Conferma
                </button>
                <button
                  onClick={() => setConfirmReset(false)}
                  className="flex-1 h-10 border border-zinc-700 text-zinc-400 font-head text-sm font-bold uppercase tracking-wide active:scale-[0.98] transition-transform"
                >
                  Annulla
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirmReset(true)}
              className="w-full h-11 flex items-center justify-center gap-2 border border-zinc-800 text-zinc-500 font-head text-sm font-bold uppercase tracking-wide active:scale-[0.98] transition-transform"
            >
              <RotateCcw size={16} /> Ricomincia sessione
            </button>
          )
        )}

        {day.exercises.map((exercise, i) => {
          const id = itemId(day.id, i);
          const keyId = exercise.keyId;
          const mastered =
            keyId && exercise.criteria
              ? CRIT_KEYS.every((k) => completed.has(`${levelId}-${keyId}-${k}`))
              : false;

          // Progression exercise handling
          if (exercise.progressionId) {
            const pid = exercise.progressionId;
            const v1done = completed.has(`${pid}-v1-done`);
            const v2done = completed.has(`${pid}-v2-done`);
            const variantIndex = v2done ? 2 : v1done ? 1 : 0;
            const variant = exercise.variants[variantIndex];
            const isLast = variantIndex === exercise.variants.length - 1;
            const mergedExercise = {
              ...exercise,
              name: variant.name,
              desc: variant.desc,
              cues: variant.cues,
              errors: variant.errors,
              images: variant.images || [],
            };
            const unlockItemId = `${pid}-v${variantIndex + 1}-done`;
            const showFlash = unlockedMsg === pid;

            return (
              <div key={i} className="space-y-2.5">
                <ExerciseCard
                  exercise={mergedExercise}
                  index={i}
                  checked={completed.has(id)}
                  onToggle={(done) => toggleItem(id, done)}
                  onTimer={(ex) => setTimerExercise(ex)}
                  mastered={false}
                />
                {showFlash && (
                  <div className="flex items-center gap-2 border border-[#00f0ff]/50 bg-[#00f0ff]/10 px-4 py-2.5">
                    <span className="text-[#00f0ff] text-sm font-bold uppercase tracking-wide">✓ Livello sbloccato!</span>
                    <span className="text-xs text-zinc-400">Continua con la nuova variante</span>
                  </div>
                )}
                {!isLast && !showFlash && (
                  <button
                    onClick={() => {
                      toggleItem(unlockItemId, true);
                      setUnlockedMsg(pid);
                      setTimeout(() => setUnlockedMsg(null), 3000);
                    }}
                    className="w-full text-left border border-zinc-700 bg-[#141414] px-4 py-3 flex items-start gap-3 active:scale-[0.99] transition-transform"
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border-2 border-zinc-600" />
                    <span className="text-sm text-zinc-400 leading-snug">{variant.unlockLabel} — <span className="text-zinc-500 uppercase tracking-wide text-[11px] font-bold">sblocca il livello successivo</span></span>
                  </button>
                )}
              </div>
            );
          }

          return (
            <div key={i} className="space-y-2.5">
              <ExerciseCard
                exercise={exercise}
                index={i}
                checked={completed.has(id)}
                onToggle={(done) => toggleItem(id, done)}
                onTimer={(ex) => setTimerExercise(ex)}
                mastered={mastered}
              />
              {keyId && exercise.criteria && (
                <MasteryCheck
                  levelId={levelId}
                  keyId={keyId}
                  criteria={exercise.criteria}
                  completed={completed}
                  onToggle={toggleItem}
                />
              )}
            </div>
          );
        })}

        {program?.progressionType !== "mastery" && (
          <button
            data-testid="session-complete-all"
            onClick={() => setManyItems(ids, !allDone)}
            className={`w-full h-14 mt-3 flex items-center justify-center gap-2 font-head text-lg font-bold uppercase tracking-wide active:scale-[0.98] transition-transform ${
              allDone
                ? "border border-zinc-800 text-zinc-400"
                : "bg-[#fa003f] text-white"
            }`}
          >
            {allDone ? (
              <>
                <RotateCcw size={20} /> Azzera giornata
              </>
            ) : (
              <>
                <CheckCheck size={22} /> Completa tutto
              </>
            )}
          </button>
        )}

        {program?.progressionType === "mastery" && !day.isBonus && (
          confirmConclude ? (
            <div className="border border-zinc-700 bg-[#141414] px-4 py-3 space-y-2 mt-3">
              <p className="text-sm text-zinc-300 leading-snug">
                Concludere questa sessione?{" "}
                <span className="text-zinc-500">
                  Le spunte verranno azzerate — la prossima volta tocca alla Sessione{" "}
                  {day.id.endsWith("-b") ? "A" : "B"}.
                </span>
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleConclude}
                  className="flex-1 h-10 bg-[#fa003f] text-white font-head text-sm font-bold uppercase tracking-wide active:scale-[0.98] transition-transform"
                >
                  Conferma
                </button>
                <button
                  onClick={() => setConfirmConclude(false)}
                  className="flex-1 h-10 border border-zinc-700 text-zinc-400 font-head text-sm font-bold uppercase tracking-wide active:scale-[0.98] transition-transform"
                >
                  Annulla
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirmConclude(true)}
              className="w-full h-11 mt-3 flex items-center justify-center gap-2 border border-zinc-700 text-zinc-400 font-head text-sm font-bold uppercase tracking-wide active:scale-[0.98] transition-transform"
            >
              <CheckCheck size={16} /> Concludi sessione
            </button>
          )
        )}

      </div>

      {timerExercise && (
        <HoldTimer exercise={timerExercise} onClose={() => setTimerExercise(null)} />
      )}
    </div>
  );
}
